<template>
    <div class="layout-preview-block relative w-full h-full">
        <div
            v-if="headerCaption"
            class="flex items-center justify-between mb-4 main-text-color"
        >
            {{ $t('layout.preview') }}
            <div
                class="flex items-center text-primary-300 hover:text-primary cursor-pointer"
                @click="toggleRealTimePreview"
            >
                <template v-if="realTimePreview">
                    <i class="vc-icon-eye icon-base mx-1" />
                    {{ $t('layout.seeWithoutChanges') }}
                </template>
                <template v-if="!realTimePreview">
                    <i class="vc-icon-blind icon-base mx-1" />
                    {{ $t('layout.seeWithChanges') }}
                </template>
            </div>
        </div>
        <div
            ref="fullscreenContainer"
            class="relative bg-main-bg main-text-color w-full h-full"
            :class="{'full-screen': isFullscreenMode}"
        >
            <div
                ref="previewWrapper"
                class="border border-ui-lines rounded preview-wrapper"
            >
                <div
                    id="dashboard-wrapper-preview"
                    class="content relative min-w-screen"
                    :style="getStyles"
                >
                    <DashboardSimplePreview
                        :dashboard="currentDashboard"
                        :layout="usedLayout"
                    />
                </div>
            </div>
            <div class="absolute right-0 bottom-0 cursor-pointer leading-none z-50 bg-main-bg shadow-base rounded m-2 text-secondary-actions py-1 px-2">
                <div class="flex items-center gap-2">
                    <span
                        class="flex-center"
                        @click="onZoomIn"
                    >
                        <i
                            class="vc-icon-zoom-in hover:text-primary icon-base"
                        />
                    </span>
                    <span
                        class="flex-center"
                        @click="onZoomOut"
                    >
                        <i
                            class="vc-icon-zoom-out hover:text-primary icon-base"
                        />
                    </span>
                    <span
                        class="flex-center"
                        @click="onFullScreen"
                    >
                        <RedesignBaseIcon
                            v-if="isFullscreenMode"
                            :size="16"
                            name="Shrink"
                            class="w-4 h-4 hover:text-primary"
                        />
                        <RedesignBaseIcon
                            v-else
                            :size="16"
                            name="Expand"
                            class="w-4 h-4 hover:text-primary"
                        />
                    </span>
                </div>
            </div>
        </div>
        <slot name="actions" />
    </div>
</template>
<script setup lang="ts">
import DashboardSimplePreview from '@/components/RedesignComponents/Dashboard/Common/DashboardSimplePreview.vue'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import {
    getActiveLayout,
    getLayoutColors,
    getLayoutFontSize,
    getLayoutRootVariables
} from '@/composables/globalData/useLayouts'
import { getPreviewLayout } from '@/composables/usePreviewLayout'
import { IDashboard } from '@/types/dashboard'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { useFullscreen } from '@vueuse/core'

const MAX_ZOOM = 2
const ZOOM_STEP = 0.1

/* Props */
const props = withDefaults(
    defineProps<{
        currentDashboard?: IDashboard
        defaultZoom?: number
        headerCaption?: boolean
        minZoom?: number
    }>(),
    {
        currentDashboard: undefined,
        headerCaption: true,
        defaultZoom: 1,
        minZoom: 0.5
    })

const emit = defineEmits<{
    (e: 'on-real-time-preview', value: boolean): void
}>()

/* Data */
const realTimePreview = ref(true)
const zoom = ref(props.defaultZoom)
const previewWrapper = ref<HTMLDivElement | null>(null)
const fullscreenContainer = ref<HTMLDivElement | null>(null)
const mousedownListener = ref<((event: MouseEvent) => void) | null>(null)

const { isFullscreen: isFullscreenMode, toggle: toggleFullscreen } = useFullscreen(fullscreenContainer)

/* Computed */
const usedLayout = computed(() => {
    return !realTimePreview.value ? getActiveLayout() : getPreviewLayout() || getActiveLayout()
})
const layoutColors = computed(() => {
    return getLayoutColors(usedLayout.value)
})
const layoutFontSize = computed(() => {
    return getLayoutFontSize(usedLayout.value)
})
const getStyles = computed(() => {
    const colors = layoutColors.value
    const fontSize = layoutFontSize.value
    const rootVariables = getLayoutRootVariables(colors, fontSize)

    return {
        ...rootVariables,
        zoom: zoom.value
    }
})

/* Methods */
function toggleRealTimePreview () {
    realTimePreview.value = !realTimePreview.value
    emit('on-real-time-preview', realTimePreview.value)
}
function onZoomIn () {
    if (zoom.value > MAX_ZOOM - ZOOM_STEP) {
        return
    }
    zoom.value += ZOOM_STEP
}
function onZoomOut () {
    if (zoom.value < props.minZoom) {
        return
    }
    zoom.value -= ZOOM_STEP
}

function handleFullscreenChange () {
    if (!isFullscreenMode.value) {
        nextTick(() => {
            zoom.value = props.defaultZoom
        })
    } else {
        zoom.value = 1
    }
}

async function onFullScreen () {
    await toggleFullscreen()
}

function handleEscape (e: KeyboardEvent) {
    if (e.code === 'Escape' && isFullscreenMode.value) {
        toggleFullscreen()
    }
}
function handleDragToScroll () {
    const wrapper = previewWrapper.value
    if (!wrapper) {
        return
    }
    wrapper.style.cursor = 'grab'

    let positions = {
        top: 0,
        left: 0,
        x: 0,
        y: 0
    }

    const mouseDownHandler = function (e: MouseEvent) {
        wrapper.style.cursor = 'grabbing'
        wrapper.style.userSelect = 'none'

        positions = {
            left: wrapper.scrollLeft,
            top: wrapper.scrollTop,
            // Get the current mouse position
            x: e.clientX,
            y: e.clientY,
        }

        document.addEventListener('mousemove', mouseMoveHandler)
        document.addEventListener('mouseup', mouseUpHandler)
    }

    const mouseMoveHandler = function (e: MouseEvent) {
        // How far the mouse has been moved
        const dx = e.clientX - positions.x
        const dy = e.clientY - positions.y
        // Scroll the element
        wrapper.scrollTop = positions.top - dy
        wrapper.scrollLeft = positions.left - dx
    }
    const mouseUpHandler = function () {
        wrapper.style.cursor = 'grab'
        wrapper.style.removeProperty('user-select')

        document.removeEventListener('mousemove', mouseMoveHandler)
        document.removeEventListener('mouseup', mouseUpHandler)
    }
    // Attach the handler
    wrapper.addEventListener('mousedown', mouseDownHandler)
    mousedownListener.value = mouseDownHandler
}

watch(isFullscreenMode, handleFullscreenChange)

onMounted(() => {
    document.addEventListener('keyup', handleEscape)
    handleDragToScroll()
})

onUnmounted(() => {
    document.removeEventListener('keyup', handleEscape)

    if (isFullscreenMode.value) {
        toggleFullscreen()
    }

    if (mousedownListener.value) {
        document.removeEventListener('mousedown', mousedownListener.value)
    }
})
</script>
<style lang="scss">
.preview-wrapper {
    @apply overflow-hidden w-full h-full;

    .content {
        zoom: 0.5;
    }
}

.full-screen {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    z-index: 99999;

    .preview-wrapper {
        height: 100vh;
        overflow: auto;
    }
}
</style>
